import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  WarrantyRegistrationFlatTeamSettingGroupById,
  type WarrantyRegistrationFlatTeamSettingGroupId,
} from "./groups";

type WarrantyRegistrationSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: WarrantyRegistrationSettingInput,
): WarrantyRegistrationSettingInput {
  return metadata;
}

function warrantyRegistrationGroup<
  const Group extends WarrantyRegistrationFlatTeamSettingGroupId,
  const T extends Record<string, WarrantyRegistrationSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const WarrantyRegistrationRegistry = {
  ...warrantyRegistrationGroup("warranty_registration.purchase_channels", {
    "warranties/purchaseChannels": setting({
      label: "Purchase channels",
      description:
        "Purchase channels available to customers during warranty registration, each mapping a display name to a channel.",
      synonyms: [
        "purchase channels",
        "registration channels",
        "warranty registration channels",
      ],
    }),
  }),
  ...warrantyRegistrationGroup(
    "warranty_registration.registration_collections",
    {
      "warranties/purchaseChannelRegistrationCollection": setting({
        label: "Registration collections",
        description:
          "Mapping of each purchase channel to a Shopify collection of products eligible for warranty registration.",
        synonyms: [
          "registration collections",
          "eligible collections",
          "warranty eligible products",
        ],
      }),
    },
  ),
};

type WarrantyRegistrationFlatTeamSettingKey =
  keyof typeof WarrantyRegistrationRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const WarrantyRegistrationFlatTeamSettingKeys = Object.keys(
  WarrantyRegistrationRegistry,
) as WarrantyRegistrationFlatTeamSettingKey[];

export const WarrantyRegistrationFlatTeamSettingCatalogDefinitions =
  WarrantyRegistrationFlatTeamSettingKeys.map((key) => {
    const definition = WarrantyRegistrationRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: WarrantyRegistrationFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    WarrantyRegistrationFlatTeamSettingKey,
    WarrantyRegistrationFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<WarrantyRegistrationFlatTeamSettingKey>
  >[];

export const WarrantyRegistrationFlatTeamSettingMetadataByKey =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Object.fromEntries(
    WarrantyRegistrationFlatTeamSettingCatalogDefinitions.map((definition) => [
      definition.id,
      definition.metadata,
    ]),
  ) as Record<WarrantyRegistrationFlatTeamSettingKey, SettingMetadata>;
